import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, Gift, Flag } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function GiveawaysPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ channel_id: "", prize: "", duration_minutes: 60, winners_count: 1, description: "" });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/giveaways/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async (e) => {
    e.preventDefault();
    if (!form.channel_id || !form.prize.trim()) return;
    try {
      await api.post(`/features/giveaways/${active.id}`, form);
      toast.success("Giveaway started 🎉");
      setForm({ channel_id: "", prize: "", duration_minutes: 60, winners_count: 1, description: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const end = async (id) => {
    try {
      const { data } = await api.post(`/features/giveaways/${active.id}/${id}/end`);
      toast.success(`Ended! Winners: ${data.winners.length}`);
      refresh();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="giveaways-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><Gift className="inline w-4 h-4 mr-2 -mt-1" />GAMES & FUN</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Giveaways</h1>
        <p className="text-zinc-400 mt-2">Run prize giveaways with reaction-based entries. Random winner picker.</p>
      </div>

      <form onSubmit={create} className="tactical-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gw-form">
        <div>
          <label className="overline block mb-2">Channel</label>
          <select className="tactical-input" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="gw-channel">
            <option value="">— Select —</option>
            {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Prize</label>
          <input className="tactical-input" placeholder="Discord Nitro / $20 Gift Card" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} data-testid="gw-prize" />
        </div>
        <div>
          <label className="overline block mb-2">Duration (minutes)</label>
          <input type="number" min="1" className="tactical-input" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} data-testid="gw-duration" />
        </div>
        <div>
          <label className="overline block mb-2">Winners</label>
          <input type="number" min="1" className="tactical-input" value={form.winners_count} onChange={(e) => setForm({ ...form, winners_count: parseInt(e.target.value) || 1 })} data-testid="gw-winners" />
        </div>
        <div className="md:col-span-2">
          <label className="overline block mb-2">Description (optional)</label>
          <textarea className="tactical-input" placeholder="Rules / extras" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <button type="submit" className="btn-primary" data-testid="gw-create-btn"><Plus className="w-4 h-4" /> Start Giveaway</button>
        </div>
      </form>

      <div className="tactical-card p-6">
        <h2 className="font-heading text-xl font-bold uppercase mb-4">All Giveaways ({list.length})</h2>
        <div className="space-y-2">
          {list.map((g) => (
            <div key={g.id} className="border border-brand-line p-3 rounded-lg flex items-center gap-4" data-testid={`gw-row-${g.id}`}>
              <Gift className="w-5 h-5 text-brand-orange" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{g.prize}</div>
                <div className="text-xs text-zinc-500">
                  {g.winners_count} winner(s) · {g.ended ? `Ended (${g.winners.length} picked)` : `Ends ${new Date(g.ends_at).toLocaleString()}`}
                </div>
              </div>
              {!g.ended && (
                <button onClick={() => end(g.id)} className="btn-secondary text-xs"><Flag className="w-3.5 h-3.5" /> End now</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
